// Opens the hike overview's Embed map and Itinerary modals from the hikes list,
// for whichever hike's ⋮ menu was used. On the overview each is mounted once for
// the page's one hike from a window config written by the ERB; here the hike
// changes per click, so the config is written per click instead.
import { createApp, type App } from 'vue';
import { Modal } from 'bootstrap';
import { i18n } from '../../i18n';
import { hikeEmbedBaseUrl } from './helpers/hikeMenu';

interface ModalHike {
  id: string | number;
  name?: string | null;
  slug?: string | null;
  obfuscatedSlug?: string | null;
  madeOfficialAt?: string | null;
  mainCountry?: { slug?: string | null } | null;
  mainRegion?: { slug?: string | null } | null;
  hikeDays?: unknown[] | null;
}

/** The window.embedBuilderConfig the overview's ERB writes, built for a list hike. */
export function embedBuilderConfigFor(hike: ModalHike, origin: string) {
  return {
    hikeId: String(hike.id),
    embedBaseUrl: hikeEmbedBaseUrl(hike, origin),
    isMultiDay: (hike.hikeDays?.length ?? 0) > 1,
  };
}

// Typed locally: entrypoints/hikes.ts and EmbedBuilderModal.vue each declare these
// on Window already, and a third declaration would have to match both exactly.
const win = window as unknown as {
  embedBuilderConfig?: ReturnType<typeof embedBuilderConfigFor>;
  openItineraryModal?: (target?: { hikeId: string; hikeName?: string }) => Promise<void> | void;
};

let embedApp: App | null = null;

/**
 * EmbedBuilderModal reads its config once, when it's created, so each open
 * mounts a fresh one for the clicked hike rather than reusing the last.
 */
export async function openEmbedBuilderForHike(hike: ModalHike): Promise<void> {
  win.embedBuilderConfig = embedBuilderConfigFor(hike, window.location.origin);

  const previous = document.getElementById('embed-builder-modal');
  if (previous) Modal.getInstance(previous)?.dispose();
  embedApp?.unmount();

  // Not #embed-builder-app: embed_builder/main.ts mounts itself onto that id.
  let host = document.getElementById('hikes-list-embed-builder');
  if (!host) {
    host = document.createElement('div');
    host.id = 'hikes-list-embed-builder';
    document.body.appendChild(host);
  }

  const { default: EmbedBuilderModal } = await import('../embed_builder/EmbedBuilderModal.vue');
  embedApp = createApp(EmbedBuilderModal);
  embedApp.use(i18n);
  embedApp.mount(host);

  const modalEl = document.getElementById('embed-builder-modal');
  if (modalEl) Modal.getOrCreateInstance(modalEl).show();
}

/** Mounts the overview's Itinerary app once, then opens it for the clicked hike. */
export async function openItineraryForHike(hike: ModalHike): Promise<void> {
  if (typeof win.openItineraryModal !== 'function') {
    const { createItineraryOverviewApp } = await import('../itinerary_overview/main');
    // Not #itinerary-overview-app: main.ts mounts itself onto that id.
    const host = document.createElement('div');
    host.id = 'hikes-list-itinerary';
    document.body.appendChild(host);
    createItineraryOverviewApp().mount(host);
  }
  await win.openItineraryModal?.({ hikeId: String(hike.id), hikeName: hike.name ?? '' });
}
